import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import ModuleList from './ModuleList';
import AssignmentList from './AssignmentList';
import { Disclosure, Transition } from '@headlessui/react';
import {
    ChartBarIcon,
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    ClockIcon,
    BookOpenIcon,
    ClipboardDocumentCheckIcon,
    QuestionMarkCircleIcon,
    UsersIcon,
    MagnifyingGlassIcon,
    Squares2X2Icon,
    ListBulletIcon,
    ChevronDownIcon,
    CalendarDaysIcon,
    UserMinusIcon,
    DocumentTextIcon,
    ScaleIcon,
} from '@heroicons/react/24/outline';
// import AttendanceRegistry from '../../components/teacher/AttendanceRegistry';
import QuizBuilder from '../../components/teacher/QuizBuilder';
import GradeBook from '../../components/teacher/GradeBook';
import Modal from '../../components/common/Modal';
import TermSelector from '../../components/common/TermSelector';
import CourseEnrollmentModal from '../../components/teacher/CourseEnrollmentModal';
import RecordOfWorkTab from '../../components/teacher/RecordOfWorkTab';
import LessonPlansTab from '../../components/teacher/LessonPlansTab';
import TemplateLibrary from '../../components/teacher/TemplateLibrary';
import SchemeOfWorkManagement from '../admin/SchemeOfWorkManagement';
import TeacherCourseDisciplineTab from '../../components/teacher/TeacherCourseDisciplineTab';
import { useAppState } from '../../context/AppStateContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlusIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PremiumTooltip from '../../components/common/PremiumTooltip';
import TeacherAiAssistantDrawer from '../../components/teacher/TeacherAiAssistantDrawer';

const SidebarItem = ({ id, label, icon: Icon, active, collapsed, onClick }) => {
    const buttonContent = (
        <button
            onClick={() => onClick(id)}
            className={`w-full flex items-center p-3 mb-2 rounded-xl transition-all group ${active
                ? 'bg-[#18216D] text-white shadow-lg shadow-indigo-900/30'
                : 'text-slate-500 hover:bg-slate-50 hover:text-[#18216D]'
                }`}
        >
            <div className={`flex items-center justify-center ${collapsed ? 'w-full' : ''}`}>
                <Icon className={`w-6 h-6 transition-transform group-hover:scale-110 ${active ? 'text-[#FFD700]' : 'text-slate-400 group-hover:text-[#18216D]'}`} />
            </div>
            {!collapsed && (
                <span className="ml-3 font-bold text-sm tracking-tight whitespace-nowrap overflow-hidden transition-all duration-300">
                    {label}
                </span>
            )}
            {active && !collapsed && (
                <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full"></div>
            )}
        </button>
    );

    if (collapsed) {
        return <PremiumTooltip text={label} position="right" wrapperClassName="relative w-full block">{buttonContent}</PremiumTooltip>;
    }
    return buttonContent;
};

const Sidebar = ({ activeTab, onChange, collapsed }) => {
    const items = [
        { id: 'sow', label: 'Scheme of Work', icon: Squares2X2Icon },
        { id: 'content', label: 'Curriculum Strands', icon: BookOpenIcon },
        { id: 'assignments', label: 'Assessments', icon: ClipboardDocumentCheckIcon },
        { id: 'quizzes', label: 'Quizzes', icon: QuestionMarkCircleIcon },
        { id: 'gradebook', label: 'Competency Grades', icon: ChartBarIcon },
        { id: 'records', label: 'Lesson Records', icon: DocumentTextIcon },
        { id: 'discipline', label: 'Character', icon: ScaleIcon },
        { id: 'students', label: 'Students', icon: UsersIcon },
    ];

    return (
        <div className={`flex flex-col h-full bg-white border-r border-gray-100 transition-all duration-300 z-50 ${collapsed ? 'hidden md:flex md:w-20' : 'absolute md:relative w-64 md:w-72 shadow-2xl md:shadow-none'}`}>
            <div className="p-4 flex-1 overflow-visible">
                {items.map((item) => (
                    <SidebarItem
                        key={item.id}
                        {...item}
                        active={activeTab === item.id}
                        collapsed={collapsed}
                        onClick={onChange}
                    />
                ))}
            </div>

            <div className="p-4 border-t border-gray-100">
                {collapsed ? (
                    <PremiumTooltip text="Exit Area" position="right" wrapperClassName="relative w-full block">
                        <Link
                            to="/teacher/courses"
                            className="flex items-center p-3 text-gray-400 hover:text-red-500 transition-colors group w-full"
                        >
                            <div className="flex items-center justify-center w-full">
                                <svg className="w-6 h-6 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
                                </svg>
                            </div>
                        </Link>
                    </PremiumTooltip>
                ) : (
                    <Link
                        to="/teacher/courses"
                        className="flex items-center p-3 text-gray-400 hover:text-red-500 transition-colors group"
                    >
                        <div className="flex items-center justify-center">
                            <svg className="w-6 h-6 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
                            </svg>
                        </div>
                        <span className="ml-3 font-bold text-sm">Exit Area</span>
                    </Link>
                )}
            </div>
        </div>
    );
};

const TeacherCourseView = () => {
    const { id } = useParams();
    const { get, post, delete: del } = useApi();
    // const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('content');
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth < 768);
    const [showQuizBuilder, setShowQuizBuilder] = useState(false);
    const [editingQuiz, setEditingQuiz] = useState(null);
    const [selectedLessonForQuiz, setSelectedLessonForQuiz] = useState(null);
    const [quizToDelete, setQuizToDelete] = useState(null);
    const [quizViewMode, setQuizViewMode] = useState('grid');
    const [recordsSubTab, setRecordsSubTab] = useState('lesson-plans'); // 'lesson-plans' or 'records'
    const [studentToUnenroll, setStudentToUnenroll] = useState(null);
    const [isDeletingQuiz, setIsDeletingQuiz] = useState(false);
    const [isUnenrollingStudent, setIsUnenrollingStudent] = useState(false);
    const [selectedTerm, setSelectedTerm] = useState('');
    const [studentSearchTerm, setStudentSearchTerm] = useState('');
    const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
    const [bandCounts, setBandCounts] = useState({ EE: 0, ME: 0, AE: 0, BE: 0 });
    const { showToast } = useAppState();
    const queryClient = useQueryClient();

    useEffect(() => {
        const handleTabChange = (e) => {
            if (e.detail) setActiveTab(e.detail);
        };
        window.addEventListener('change-course-tab', handleTabChange);
        return () => window.removeEventListener('change-course-tab', handleTabChange);
    }, []);

    // Support for Tab Deep-Linking (e.g., ?tab=quizzes)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        if (tab) setActiveTab(tab);
        // ?sub= picks the Lesson Records sub-tab (used when returning from the lesson plan editor)
        const sub = params.get('sub');
        if (tab === 'records' && ['lesson-plans', 'records', 'rubrics'].includes(sub)) {
            setRecordsSubTab(sub);
        }
    }, []);

    // Auto-collapse sidebar on mobile when switching tabs
    useEffect(() => {
        if (window.innerWidth < 768) {
            setIsSidebarCollapsed(true);
        }
    }, [activeTab]);

    // Cache learning area data for 5 min — revisiting the same area shows data instantly
    const { data: course, isLoading: loading, isFetching: courseFetching } = useQuery({
        queryKey: ['course', id, selectedTerm],
        queryFn: async () => {
            const termParam = selectedTerm ? `?term_id=${selectedTerm}` : '';
            const courseResponse = await get(`/teachers/api/courses/${id}/${termParam}`);
            return courseResponse;
        },
        staleTime: 5 * 60 * 1000,
    });

    // Memory sorting and filtering for enrolled students
    const filteredEnrolledStudents = React.useMemo(() => {
        if (!course?.enrolled_students) return [];
        return course.enrolled_students
            .filter(student =>
                student.name?.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
                student.student_id?.toString().toLowerCase().includes(studentSearchTerm.toLowerCase())
            )
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [course?.enrolled_students, studentSearchTerm]);

    // Fetch terms
    const { data: termsData } = useQuery({
        queryKey: ['academic-terms'],
        queryFn: () => get('/api/academic-terms/'),
        staleTime: 60 * 60 * 1000,
    });

    const terms = React.useMemo(() => termsData || [], [termsData]);

    // Set default term if not set (only once)
    useEffect(() => {
        if (terms.length > 0 && !selectedTerm) {
            const currentTerm = terms.find(t => t.is_current);
            if (currentTerm) {
                setSelectedTerm(currentTerm.id.toString());
            } else {
                setSelectedTerm(terms[0].id.toString());
            }
        }
    }, [terms, selectedTerm]);

    // Fetch discipline records for top dynamic stat cards
    const { data: courseDisciplineRecords = [] } = useQuery({
        queryKey: ['course-discipline-records', id],
        queryFn: async () => {
            const res = await get(`/api/discipline/records/?course_id=${id}`);
            return Array.isArray(res) ? res : [];
        },
        enabled: !!id,
        staleTime: 30 * 1000,
    });

    const disciplineStats = React.useMemo(() => {
        const totalRecorded = courseDisciplineRecords.length;
        const meritPoints = courseDisciplineRecords
            .filter(r => r.category_type === 'merit')
            .reduce((sum, r) => sum + (r.points || 0), 0);
        const demeritPoints = courseDisciplineRecords
            .filter(r => r.category_type === 'demerit')
            .reduce((sum, r) => sum + (r.points || 0), 0);
        return { totalRecorded, meritPoints, demeritPoints };
    }, [courseDisciplineRecords]);

    const lessons = (course?.modules || []).flatMap(m => m.sub_strands || []);
    const quizzes = course?.quizzes || [];

    const handleCreateQuiz = () => {
        if (lessons.length === 0) {
            alert('Please create at least one lesson first to attach a quiz to.');
            return;
        }
        setEditingQuiz(null);
        setSelectedLessonForQuiz(lessons[0].id);
        setShowQuizBuilder(true);
    };

    const handleEditQuiz = (quiz) => {
        setEditingQuiz(quiz);
        setSelectedLessonForQuiz(quiz.lesson);
        setShowQuizBuilder(true);
    };

    const handleDeleteQuiz = async (quizId) => {
        setQuizToDelete(quizId);
    };

    const confirmDeleteQuiz = async () => {
        if (!quizToDelete) return;
        try {
            setIsDeletingQuiz(true);
            await del(`/teachers/api/quizzes/${quizToDelete}/`);
            showToast('Quiz Deleted Successfully', 'success');
            setQuizToDelete(null);
            queryClient.invalidateQueries({ queryKey: ['course', id] });
        } catch (error) {
            console.error('Error deleting quiz:', error);
            showToast('Failed to delete quiz', 'error');
        } finally {
            setIsDeletingQuiz(false);
        }
    };

    const handleUnenrollStudent = (student) => {
        setStudentToUnenroll(student);
    };

    const confirmUnenrollStudent = async () => {
        if (!studentToUnenroll) return;
        try {
            setIsUnenrollingStudent(true);
            const response = await post(`/teachers/api/courses/${id}/unenroll-student/`, {
                student_id: studentToUnenroll.id
            });
            if (response.status === 'success') {
                showToast(response.message || 'Student unenrolled successfully', 'success');
                setStudentToUnenroll(null);
                queryClient.invalidateQueries({ queryKey: ['course', id] });
            } else {
                showToast(response.error || 'Failed to unenroll student', 'error');
            }
        } catch (error) {
            console.error('Error unenrolling student:', error);
            showToast('An error occurred during unenrollment', 'error');
        } finally {
            setIsUnenrollingStudent(false);
        }
    };

    if (loading) {
        return <LoadingSpinner fullScreen message="Fetching Strands..." />;
    }

    if (!course) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="text-center bg-white p-16 rounded-[2.5rem] shadow-2xl shadow-indigo-900/10 max-w-md w-full border border-slate-50">
                    <div className="text-6xl mb-8">🔍</div>
                    <h3 className="text-2xl font-black text-[#18216D] mb-2 tracking-tight">Learning Area Not Found</h3>
                    <p className="text-slate-400 mb-10 font-medium">The subject you're looking for might have been moved or unassigned.</p>
                    <Link to="/teacher/courses" className="inline-block px-10 py-4 bg-[#18216D] text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-900/20 hover:bg-[#0D164F] transition-all">
                        ← Back to My Learning Areas
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-[calc(100vh-4rem)] bg-gray-50 overflow-hidden">
            <Sidebar
                activeTab={activeTab}
                onChange={(tab) => setActiveTab(tab)}
                collapsed={isSidebarCollapsed}
            />

            <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">
                <header className="bg-white border-b border-gray-100 shrink-0">
                    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                                className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors"
                            >
                                <svg className={`w-6 h-6 transition-transform ${isSidebarCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                                </svg>
                            </button>
                            <div className="flex items-center gap-4">
                                <div>
                                    <h1 className="text-2xl font-black text-[#18216D] tracking-tighter leading-none">
                                        {course.name}
                                    </h1>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className="px-3 py-1 text-[9px] font-black uppercase tracking-[0.2em] bg-[#FFC425]/10 text-[#18216D] rounded-full border border-[#FFC425]/20">
                                            GRADE {course.grade_level_name?.replace('Grade ', '') || '4'} ({course.stream_display_name})
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="hidden md:flex items-center space-x-3">
                            {activeTab === 'discipline' ? (
                                <>
                                    <div className="text-center bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100">
                                        <div className="text-lg font-black text-[#18216D] leading-none">{disciplineStats.totalRecorded}</div>
                                        <div className="text-[8px] font-black text-slate-400 uppercase mt-1 tracking-[0.2em]">Recorded</div>
                                    </div>
                                    <div className="text-center bg-emerald-600 px-4 py-2 rounded-2xl shadow-lg shadow-emerald-900/10">
                                        <div className="text-lg font-black text-white leading-none">+{disciplineStats.meritPoints}</div>
                                        <div className="text-[8px] font-black text-emerald-100 uppercase mt-1 tracking-[0.2em]">Merits</div>
                                    </div>
                                    <div className="text-center bg-rose-500 px-4 py-2 rounded-2xl shadow-lg shadow-rose-900/10">
                                        <div className="text-lg font-black text-white leading-none">-{disciplineStats.demeritPoints}</div>
                                        <div className="text-[8px] font-black text-rose-100 uppercase mt-1 tracking-[0.2em]">Demerits</div>
                                    </div>
                                </>
                            ) : activeTab === 'gradebook' ? (
                                <div className="flex items-center space-x-2">
                                    <div className="text-center bg-[#FFD700]/10 px-3.5 py-1.5 rounded-2xl border border-[#FFD700]/30 shadow-xs min-w-[56px]">
                                        <div className="text-base font-black text-[#B8860B] leading-none">{bandCounts.EE || 0}</div>
                                        <div className="text-[10px] font-black text-[#B8860B] uppercase mt-0.5 tracking-wider">EE</div>
                                    </div>
                                    <div className="text-center bg-[#40E0D0]/10 px-3.5 py-1.5 rounded-2xl border border-[#40E0D0]/30 shadow-xs min-w-[56px]">
                                        <div className="text-base font-black text-[#008B8B] leading-none">{bandCounts.ME || 0}</div>
                                        <div className="text-[10px] font-black text-[#008B8B] uppercase mt-0.5 tracking-wider">ME</div>
                                    </div>
                                    <div className="text-center bg-[#E97451]/10 px-3.5 py-1.5 rounded-2xl border border-[#E97451]/30 shadow-xs min-w-[56px]">
                                        <div className="text-base font-black text-[#C0392B] leading-none">{bandCounts.AE || 0}</div>
                                        <div className="text-[10px] font-black text-[#C0392B] uppercase mt-0.5 tracking-wider">AE</div>
                                    </div>
                                    <div className="text-center bg-[#808080]/10 px-3.5 py-1.5 rounded-2xl border border-[#808080]/30 shadow-xs min-w-[56px]">
                                        <div className="text-base font-black text-[#505050] leading-none">{bandCounts.BE || 0}</div>
                                        <div className="text-[10px] font-black text-[#505050] uppercase mt-0.5 tracking-wider">BE</div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="text-center bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100">
                                        <div className="text-lg font-black text-[#18216D] leading-none">{course.enrolled_students_count || 0}</div>
                                        <div className="text-[8px] font-black text-slate-400 uppercase mt-1 tracking-[0.2em]">Scholars</div>
                                    </div>
                                    <div className="text-center bg-[#18216D] px-4 py-2 rounded-2xl shadow-lg shadow-indigo-900/10">
                                        <div className="text-lg font-black text-white leading-none">{course.modules?.length || 0}</div>
                                        <div className="text-[8px] font-black text-indigo-200 uppercase mt-1 tracking-[0.2em]">Strands</div>
                                    </div>
                                    <div className="text-center bg-[#FFC425] px-4 py-2 rounded-2xl shadow-lg shadow-yellow-500/10">
                                        <div className="text-lg font-black text-[#18216D] leading-none">{course.assignments?.length || 0}</div>
                                        <div className="text-[8px] font-black text-[#18216D]/60 uppercase mt-1 tracking-[0.2em]">Tasks</div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </header>

                <main className="flex-1 p-8 relative">
                    <div className="max-w-6xl mx-auto">
                        {activeTab === 'content' && (
                            <ModuleList courseId={id} initialModules={course.modules} />
                        )}

                        {activeTab === 'assignments' && (
                            <AssignmentList courseId={id} initialAssignments={course.assignments} />
                        )}

                        {activeTab === 'records' && recordsSubTab === 'lesson-plans' && (
                            <LessonPlansTab courseId={id} subTab={recordsSubTab} onSubTabChange={setRecordsSubTab} />
                        )}

                        {activeTab === 'records' && recordsSubTab === 'records' && (
                            <RecordOfWorkTab courseId={id} subTab={recordsSubTab} onSubTabChange={setRecordsSubTab} />
                        )}

                        {activeTab === 'records' && recordsSubTab === 'rubrics' && (
                            <TemplateLibrary courseId={id} course={course} subTab={recordsSubTab} onSubTabChange={setRecordsSubTab} />
                        )}

                        {activeTab === 'quizzes' && (
                            <div className="space-y-10">
                                {/* Desktop Header */}
                                <div className="hidden md:flex flex-row items-center justify-between gap-6 mb-10 bg-white p-8 rounded-[2rem] border border-slate-100 shadow-sm relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                                    <div className="relative z-10 flex flex-row items-center justify-between w-full">
                                        <div>
                                            <h2 className="text-3xl font-black text-[#18216D] tracking-tighter uppercase italic">Curriculum Quizzes</h2>
                                            <p className="text-slate-400 text-sm font-medium mt-1">Interactive assessments for student evaluation</p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <TermSelector
                                                terms={terms}
                                                selectedTerm={selectedTerm}
                                                onTermChange={setSelectedTerm}
                                                isLoading={courseFetching}
                                            />
                                            <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-200">
                                                <button
                                                    onClick={() => setQuizViewMode('grid')}
                                                    className={`p-2 rounded-lg transition-all ${quizViewMode === 'grid' ? 'bg-[#18216D] text-white shadow-lg' : 'text-slate-400 hover:text-[#18216D]'}`}
                                                >
                                                    <Squares2X2Icon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => setQuizViewMode('list')}
                                                    className={`p-2 rounded-lg transition-all ${quizViewMode === 'list' ? 'bg-[#18216D] text-white shadow-lg' : 'text-slate-400 hover:text-[#18216D]'}`}
                                                >
                                                    <ListBulletIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                            <button
                                                onClick={handleCreateQuiz}
                                                className="px-6 py-3 bg-[#FFC425] text-[#18216D] rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-[#18216D] hover:text-white transition-all shadow-xl shadow-amber-900/10 flex items-center space-x-2 active:scale-95 group"
                                            >
                                                <PlusIcon className="w-5 h-5 stroke-[3] group-hover:rotate-90 transition-transform" />
                                                <span>Build Quiz</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Mobile Header */}
                                <div className="md:hidden space-y-6 mb-8">
                                    <div>
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="h-1 w-8 bg-[#FFC425] rounded-full"></div>
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Interactive Assessments</p>
                                        </div>
                                        <h1 className="text-4xl font-black text-[#18216D] tracking-tighter">Curriculum Quizzes</h1>
                                    </div>

                                    <div className="flex flex-col gap-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1">
                                                <TermSelector
                                                    terms={terms}
                                                    selectedTerm={selectedTerm}
                                                    onTermChange={setSelectedTerm}
                                                    isLoading={courseFetching}
                                                />
                                            </div>
                                            <button
                                                onClick={handleCreateQuiz}
                                                className="p-4 bg-[#FFC425] text-[#18216D] rounded-2xl shadow-lg shadow-amber-900/10 active:scale-95 transition-all"
                                            >
                                                <PlusIcon className="w-6 h-6 stroke-[3]" />
                                            </button>
                                        </div>

                                        <div className="flex bg-white p-1 rounded-2xl border border-slate-100 shadow-sm w-fit">
                                            <button
                                                onClick={() => setQuizViewMode('grid')}
                                                className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${quizViewMode === 'grid' ? 'bg-[#18216D] text-white shadow-md' : 'text-slate-400'}`}
                                            >
                                                Grid
                                            </button>
                                            <button
                                                onClick={() => setQuizViewMode('list')}
                                                className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${quizViewMode === 'list' ? 'bg-[#18216D] text-white shadow-md' : 'text-slate-400'}`}
                                            >
                                                List
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {quizzes.length === 0 ? (
                                    <div className="text-center py-24 bg-white rounded-[2.5rem] border border-slate-100 shadow-xl shadow-indigo-900/5">
                                        <div className="w-24 h-24 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-8 shadow-inner">
                                            <QuestionMarkCircleIcon className="h-10 w-10 text-slate-300" />
                                        </div>
                                        <h3 className="text-2xl font-black text-[#18216D] mb-2 tracking-tight">Build Your Assessment</h3>
                                        <p className="text-slate-400 max-w-md mx-auto mb-10 font-medium italic">Create interactive quizzes to evaluate student competencies and automate your grading workflow.</p>
                                        <button
                                            onClick={handleCreateQuiz}
                                            className="px-10 py-4 bg-[#18216D] text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-[#0D164F] transition-all shadow-xl shadow-indigo-900/20"
                                        >
                                            Configure First Quiz
                                        </button>
                                    </div>
                                ) : quizViewMode === 'grid' ? (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in fade-in duration-500">
                                        {quizzes.map((quiz) => (
                                            <div key={quiz.id} className="bg-white rounded-3xl border border-gray-100 p-6 hover:shadow-xl hover:shadow-gray-200/50 transition-all group relative overflow-hidden">
                                                <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50/50 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110"></div>
                                                <div className="relative z-10 flex flex-col h-full">
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div>
                                                            <div className="flex items-center space-x-3 mb-2">
                                                                <h3 className="text-xl font-bold text-gray-900">{quiz.title}</h3>
                                                                {quiz.is_published ? (
                                                                    <span className="px-2.5 py-1 text-[10px] font-black bg-emerald-50 text-emerald-600 rounded-full border border-emerald-100 uppercase tracking-widest">Live</span>
                                                                ) : (
                                                                    <span className="px-2.5 py-1 text-[10px] font-black bg-gray-50 text-gray-500 rounded-full border border-gray-100 uppercase tracking-widest">Draft</span>
                                                                )}
                                                            </div>

                                                            {quiz.tested_outcomes_detail?.length > 0 && (
                                                                <div className="mt-4">
                                                                    <Disclosure>
                                                                        {({ open }) => (
                                                                            <div className="w-full">
                                                                                <Disclosure.Button className="flex items-center justify-between w-full py-2 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-[#18216D] transition-colors">
                                                                                    <span>Curriculum Outcomes ({quiz.tested_outcomes_detail.length})</span>
                                                                                    <ChevronDownIcon className={`w-3 h-3 transition-transform ${open ? 'rotate-180 text-[#18216D]' : ''}`} />
                                                                                </Disclosure.Button>
                                                                                <Transition
                                                                                    enter="transition duration-100 ease-out"
                                                                                    enterFrom="transform scale-95 opacity-0"
                                                                                    enterTo="transform scale-100 opacity-100"
                                                                                    leave="transition duration-75 ease-out"
                                                                                    leaveFrom="transform scale-100 opacity-100"
                                                                                    leaveTo="transform scale-95 opacity-0"
                                                                                >
                                                                                    <Disclosure.Panel className="mt-2 space-y-2">
                                                                                        {quiz.tested_outcomes_detail.map((o, i) => (
                                                                                            <div key={i} className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-start gap-3">
                                                                                                <div className="w-1.5 h-1.5 rounded-full bg-[#18216D] mt-1.5 shrink-0"></div>
                                                                                                <span className="text-[11px] font-bold text-[#18216D] leading-relaxed italic">{o.description}</span>
                                                                                            </div>
                                                                                        ))}
                                                                                    </Disclosure.Panel>
                                                                                </Transition>
                                                                            </div>
                                                                        )}
                                                                    </Disclosure>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center space-x-6 text-xs font-black text-gray-400 mt-auto uppercase tracking-wider">
                                                        <span className="flex items-center"><ClockIcon className="w-4 h-4 mr-2" /> {quiz.time_limit_minutes > 0 ? `${quiz.time_limit_minutes} Mins` : 'Unlimited'}</span>
                                                        <span className="flex items-center"><ClipboardDocumentCheckIcon className="w-4 h-4 mr-2" /> {quiz.questions?.length || 0} Questions</span>
                                                        {quiz.due_date && (
                                                            <span className="flex items-center text-[#18216D]">
                                                                <CalendarDaysIcon className="w-4 h-4 mr-2" />
                                                                Due: {new Date(quiz.due_date).toLocaleDateString()}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="mt-6 flex items-center space-x-3">
                                                        <button
                                                            onClick={() => handleEditQuiz(quiz)}
                                                            className="flex-1 py-3 bg-gray-50 text-gray-700 rounded-xl font-black hover:bg-gray-100 transition-all text-center"
                                                        >
                                                            Edit Design
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteQuiz(quiz.id)}
                                                            className="p-3 text-red-100 hover:text-red-600 bg-red-50/10 hover:bg-red-50 rounded-xl transition-all"
                                                        >
                                                            <TrashIcon className="w-5 h-5 stroke-[2.5]" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-xl shadow-indigo-900/5 overflow-hidden animate-in fade-in duration-500">
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="bg-slate-50 border-b border-slate-100">
                                                        <th className="px-8 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Quiz Details</th>
                                                        <th className="px-8 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Due Date</th>
                                                        <th className="px-8 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Outcomes</th>
                                                        <th className="px-8 py-5 text-center text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Questions</th>
                                                        <th className="px-8 py-5 text-center text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Duration</th>
                                                        <th className="px-8 py-5 text-right text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-50">
                                                    {quizzes.map((quiz) => (
                                                        <tr key={quiz.id} className="hover:bg-indigo-50/30 transition-all group">
                                                            <td className="px-8 py-6">
                                                                <div className="flex items-center gap-3">
                                                                    <div>
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-base font-black text-[#18216D] tracking-tight">{quiz.title}</span>
                                                                            {quiz.is_published && <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse shadow-sm shadow-emerald-200" />}
                                                                            {!quiz.is_published && <span className="text-[8px] font-black text-slate-300 uppercase tracking-widest italic ml-1">(Draft)</span>}
                                                                        </div>
                                                                        {quiz.due_date && (
                                                                            <p className="text-[9px] font-bold text-slate-400 mt-0.5">
                                                                                Due: {new Date(quiz.due_date).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-8 py-6">
                                                                {quiz.due_date ? (
                                                                    <span className="text-xs font-black text-[#18216D]">
                                                                        {new Date(quiz.due_date).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">No Deadline</span>
                                                                )}
                                                            </td>
                                                            <td className="px-8 py-6">
                                                                <div className="flex flex-col gap-2 max-w-[300px]">
                                                                    {quiz.tested_outcomes_detail?.length > 0 ? (
                                                                        quiz.tested_outcomes_detail.map((o, i) => (
                                                                            <div key={i} className="flex items-start gap-2">
                                                                                <div className="w-1.5 h-1.5 rounded-full bg-[#FFC425] mt-1.5 shrink-0"></div>
                                                                                <span className="text-[10px] font-bold text-[#18216D] leading-tight italic" title={o.description}>
                                                                                    {o.description}
                                                                                </span>
                                                                            </div>
                                                                        ))
                                                                    ) : (
                                                                        <span className="text-[10px] font-bold text-slate-300 italic uppercase tracking-widest">No specific outcomes</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-8 py-6">
                                                                <div className="flex flex-col items-center">
                                                                    <p className="text-sm font-black text-[#18216D]">{quiz.questions?.length || 0}</p>
                                                                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Questions</p>
                                                                </div>
                                                            </td>
                                                            <td className="px-8 py-6">
                                                                <div className="flex flex-col items-center">
                                                                    <p className="text-sm font-black text-[#18216D]">{quiz.time_limit_minutes || '0'}</p>
                                                                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Minutes</p>
                                                                </div>
                                                            </td>
                                                            <td className="px-8 py-6 text-right">
                                                                <div className="flex items-center justify-end gap-2">
                                                                    <button
                                                                        onClick={() => handleEditQuiz(quiz)}
                                                                        className="p-2.5 text-slate-300 hover:text-[#18216D] hover:bg-white rounded-xl transition-all shadow-sm hover:shadow-md border border-transparent hover:border-slate-100"
                                                                    >
                                                                        <PencilSquareIcon className="w-4 h-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDeleteQuiz(quiz.id)}
                                                                        className="p-2.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-transparent hover:border-red-100"
                                                                    >
                                                                        <TrashIcon className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'discipline' && (
                            <TeacherCourseDisciplineTab courseId={id} course={course} />
                        )}

                        {activeTab === 'students' && (
                            <div>
                                {/* Desktop Header */}
                                <div className="hidden md:flex flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm mb-8">
                                    <div className="flex-1">
                                        <h2 className="text-2xl font-black text-[#18216D] tracking-tight">Enrolled Students</h2>
                                        <p className="text-slate-400 text-sm font-medium">Manage all learners registered in this learning area</p>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="relative group w-72">
                                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                                                <MagnifyingGlassIcon className="w-4 h-4" />
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="Search scholar name or ID..."
                                                value={studentSearchTerm}
                                                onChange={(e) => setStudentSearchTerm(e.target.value)}
                                                className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-sm font-bold text-[#18216D]"
                                            />
                                        </div>
                                        <button
                                            onClick={() => setShowEnrollmentModal(true)}
                                            className="px-6 py-3 bg-[#18216D] text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-[#0D164F] transition-all shadow-xl shadow-indigo-900/10 flex items-center space-x-2 whitespace-nowrap"
                                        >
                                            <UserPlusIcon className="w-4 h-4 stroke-[3]" />
                                            <span>Enroll Students</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Mobile Header */}
                                <div className="md:hidden space-y-6 mb-8">
                                    <div>
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="h-1 w-8 bg-[#FFC425] rounded-full"></div>
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Class Roster</p>
                                        </div>
                                        <h1 className="text-4xl font-black text-[#18216D] tracking-tighter">Enrolled Students</h1>
                                    </div>

                                    <div className="flex flex-col gap-4">
                                        <div className="relative group w-full">
                                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                                                <MagnifyingGlassIcon className="w-4 h-4" />
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="Search scholars..."
                                                value={studentSearchTerm}
                                                onChange={(e) => setStudentSearchTerm(e.target.value)}
                                                className="w-full pl-11 pr-4 py-4 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-sm font-bold text-[#18216D]"
                                            />
                                        </div>
                                        <button
                                            onClick={() => setShowEnrollmentModal(true)}
                                            className="w-full py-4 bg-[#18216D] text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-900/20 flex items-center justify-center gap-3 active:scale-95 transition-all"
                                        >
                                            <UserPlusIcon className="w-5 h-5 stroke-[3]" />
                                            <span>Enroll Students</span>
                                        </button>
                                    </div>
                                </div>

                                {filteredEnrolledStudents && filteredEnrolledStudents.length > 0 ? (
                                    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="bg-gray-50 text-left">
                                                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Full Name</th>
                                                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Admission No</th>
                                                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Pathway</th>
                                                    <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {filteredEnrolledStudents.map((student) => (
                                                    <tr key={student.id} className="hover:bg-blue-50/30 transition-colors group">
                                                        <td className="px-8 py-5">
                                                            <div className="flex items-center">
                                                                <div className="w-10 h-10 bg-[#18216D]/5 text-[#18216D] rounded-xl flex items-center justify-center font-black mr-3 uppercase border border-[#18216D]/10">
                                                                    {student.name?.substring(0, 2)}
                                                                </div>
                                                                <span className="text-sm font-black text-[#18216D]">{student.name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-8 py-5 text-sm font-black text-gray-500">{student.student_id}</td>
                                                        <td className="px-8 py-5">
                                                            <span className={`text-[10px] font-black uppercase tracking-widest ${student.career_pathway ? 'text-[#18216D]' : 'text-slate-300 italic'}`}>
                                                                {student.career_pathway ? (
                                                                    student.career_pathway === 'STEM' ? 'STEM' :
                                                                        student.career_pathway === 'SOCIAL_SCIENCES' ? 'Social Sciences' :
                                                                            student.career_pathway === 'ARTS_SPORTS' ? 'Arts & Sports' : student.career_pathway
                                                                ) : 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td className="px-8 py-5 text-right">
                                                            <button
                                                                onClick={() => handleUnenrollStudent(student)}
                                                                className="px-3.5 py-2 text-rose-600 hover:text-white bg-rose-50 hover:bg-rose-600 border border-rose-200/80 rounded-xl transition-all shadow-sm flex items-center justify-center gap-1.5 ml-auto active:scale-95 group font-black text-[10px] uppercase tracking-widest"
                                                                title={`Remove ${student.name} from class`}
                                                            >
                                                                <UserMinusIcon className="w-4 h-4 stroke-[2.5]" />
                                                                <span className="hidden sm:inline">Unenroll</span>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-24 bg-white rounded-3xl border-2 border-dashed border-gray-100 shadow-sm">
                                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                            <UsersIcon className="h-10 w-10 text-gray-300" />
                                        </div>
                                        <p className="text-gray-400 font-bold">No students are currently enrolled.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'gradebook' && (
                            <GradeBook
                                courseId={id}
                                selectedTerm={selectedTerm}
                                onTermChange={setSelectedTerm}
                                onNavigateTab={setActiveTab}
                                onBandCountsChange={setBandCounts}
                            />
                        )}

                        {/* Attendance Registry removed in favor of Class Teacher attendance */}

                        {activeTab === 'sow' && (
                            <div className="p-8">
                                <SchemeOfWorkManagement
                                    teacherMode={true}
                                    learningAreaId={course?.id}
                                    autoOpenRoadmap={true}
                                />
                            </div>
                        )}
                    </div>
                </main>
            </div>

            {/* Modals */}
            {showQuizBuilder && (
                <QuizBuilder
                    lessonId={selectedLessonForQuiz}
                    courseId={id}
                    quiz={editingQuiz}
                    onClose={() => {
                        setShowQuizBuilder(false);
                        setEditingQuiz(null);
                    }}
                    onSave={() => queryClient.invalidateQueries({ queryKey: ['course', id] })}
                />
            )}


            {/* Deletion Modal */}
            <Modal
                isOpen={!!quizToDelete}
                onClose={() => !isDeletingQuiz && setQuizToDelete(null)}
                title="Confirm Deletion"
                type="danger"
                footer={
                    <>
                        <button
                            onClick={confirmDeleteQuiz}
                            disabled={isDeletingQuiz}
                            className="px-10 py-5 bg-rose-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-rose-600 transition-all shadow-xl shadow-rose-900/20 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isDeletingQuiz ? (
                                <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-white/20 border-t-white mr-2"></div>
                                    <span>Deleting...</span>
                                </>
                            ) : (
                                <span>Delete Quiz</span>
                            )}
                        </button>
                        <button
                            onClick={() => setQuizToDelete(null)}
                            disabled={isDeletingQuiz}
                            className="px-10 py-5 bg-slate-100 text-[#18216D] rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 transition-all disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </>
                }
            >
                <div className="flex flex-col items-center text-center p-2">
                    <div className="w-20 h-20 bg-rose-50 rounded-[2.5rem] flex items-center justify-center mb-6 shadow-inner animate-in zoom-in duration-300">
                        <TrashIcon className="h-10 w-10 text-rose-500" />
                    </div>
                    <h3 className="text-lg font-black text-[#18216D] mb-2">Delete Permanently?</h3>
                    <p className="text-slate-500 font-medium leading-relaxed text-sm">
                        Are you sure you want to delete this interactive assessment?
                        <br />
                        <span className="text-rose-600 font-bold block mt-2">This will remove all questions, logic, and student attempts. This action is irreversible.</span>
                    </p>
                </div>
            </Modal>

            {/* Enrollment Modal */}
            {showEnrollmentModal && (
                <CourseEnrollmentModal
                    courseId={id}
                    courseName={course.name}
                    gradeId={course.grade_level?.id}
                    gradeName={course.grade_level?.name}
                    onClose={() => setShowEnrollmentModal(false)}
                    onRefresh={() => queryClient.invalidateQueries({ queryKey: ['course', id] })}
                />
            )}

            {/* Unenrollment Confirmation Modal */}
            <Modal
                isOpen={!!studentToUnenroll}
                onClose={() => !isUnenrollingStudent && setStudentToUnenroll(null)}
                title="Remove Student from Class"
                type="danger"
                footer={
                    <>
                        <button
                            onClick={confirmUnenrollStudent}
                            disabled={isUnenrollingStudent}
                            className="px-10 py-5 bg-rose-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-rose-600 transition-all shadow-xl shadow-rose-900/20 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isUnenrollingStudent ? (
                                <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-white/20 border-t-white mr-2"></div>
                                    <span>Removing...</span>
                                </>
                            ) : (
                                <span>Confirm Removal</span>
                            )}
                        </button>
                        <button
                            onClick={() => setStudentToUnenroll(null)}
                            disabled={isUnenrollingStudent}
                            className="px-10 py-5 bg-slate-100 text-[#18216D] rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 transition-all disabled:opacity-50"
                        >
                            Keep Student
                        </button>
                    </>
                }
            >
                <div className="flex flex-col items-center text-center">
                    <div className="w-20 h-20 bg-rose-50 rounded-[2rem] flex items-center justify-center mb-6 shadow-inner">
                        <UserMinusIcon className="h-10 w-10 text-rose-500" />
                    </div>
                    <p className="text-slate-600 font-medium leading-relaxed">
                        Are you sure you want to remove <span className="font-black text-[#18216D]">{studentToUnenroll?.name}</span> from this learning area?
                        <br /><span className="text-[11px] text-slate-400 mt-2 block italic">This will stop the student from accessing coursework and participating in assessments for this class.</span>
                    </p>
                </div>
            </Modal>

            {/* AI Assistant Floating Drawer */}
            {course && (
                <TeacherAiAssistantDrawer
                    learningAreaId={course.id}
                    activeTab={activeTab}
                    courseName={course.name}
                    gradeName={course.grade_level_name || course.grade_level}
                />
            )}

        </div>
    );
};

export default TeacherCourseView;
